"use client";

import React from "react";
import { TriangleAlert, FileWarning, ShieldAlert, ArrowRight, CircleCheck, CircleX } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const warnings = [{
  title: "Atraso no Annual Return (CRO)",
  icon: TriangleAlert,
  risk: "Perda imediata da isenção de auditoria, multas diárias acumulativas e risco real de encerramento forçado da empresa (Strike Off).",
  solution: "Monitoramento proativo de prazos e entrega garantida da documentação com antecedência."
}, {
  title: "Erros na Declaração de VAT",
  icon: FileWarning,
  risk: "Auditorias rigorosas do Revenue, penalidades financeiras severas e cobrança de juros sobre o imposto não pago ou declarado incorretamente.",
  solution: "Cálculo preciso, reconciliação detalhada de faturas e submissão em total conformidade."
}, {
  title: "Registro Incorreto (Self-Employed)",
  icon: ShieldAlert,
  risk: "Pagamento de impostos desnecessários, enquadramento fiscal inadequado e falha na conformidade legal desde o primeiro dia de operação.",
  solution: "Orientação especializada para abertura, estruturação correta e planejamento tributário eficiente."
}];
export default function EviteMultasEErrosFiscais() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="evite-multas-e-erros-fiscais" className="dark bg-background text-foreground relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-destructive/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            O Alto Custo do <span className="text-destructive">Descuido Fiscal</span> na Irlanda
          </h2>
          <p className="text-lg text-muted-foreground">
            A legislação fiscal irlandesa é rigorosa. Pequenos erros ou atrasos podem gerar problemas graves e custos desnecessários para o seu negócio. Ter uma contabilidade especializada não é um luxo, é uma medida de segurança.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {warnings.map((item, i) => <div key={i} data-index={i} className={`transition-all duration-700 ease-out delay-${i * 200} ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <Card className="h-full bg-card/50 backdrop-blur-sm border-destructive/30 shadow-lg shadow-destructive/10 hover:shadow-destructive/20 hover:border-destructive/50 transition-all duration-300 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-destructive/80 to-destructive/20 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                
                <CardHeader className="pb-4">
                  <div className="size-12 rounded-lg bg-destructive/10 flex items-center justify-center mb-4 border border-destructive/20">
                    <item.icon className="size-6 text-destructive" />
                  </div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-destructive font-semibold text-sm uppercase tracking-wider">
                      <CircleX className="size-4" />
                      <span>O Risco</span>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {item.risk}
                    </p>
                  </div>
                  
                  <Separator className="bg-border/50" />
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-primary font-semibold text-sm uppercase tracking-wider">
                      <CircleCheck className="size-4" />
                      <span>A Solução KRB</span>
                    </div>
                    <p className="text-foreground/90 text-sm leading-relaxed">
                      {item.solution}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>

        <div className="text-center flex flex-col items-center justify-center">
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Não deixe a saúde financeira da sua empresa em risco. Nossa equipe de especialistas em legislação irlandesa está pronta para organizar suas obrigações.
          </p>
          <Button asChild size="lg" className="group">
            <Link to="#contato">
              Revisar Minha Situação Fiscal
              <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-destructive/30 via-border to-transparent" />
    </section>;
}